import React, { ReactNode, useRef, useState, useEffect } from "react";
import {
  Button,
  FormControl,
  Icon,
  InputGroup,
  Flex,
  Box,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  Select,
  Table,
  Tbody,
  Tr,
  Td,
  ModalFooter
} from "@chakra-ui/react";
import { useForm, UseFormRegisterReturn } from "react-hook-form";

//react icons
import { FiFile } from "react-icons/fi";
import { AiOutlineUpload } from "react-icons/ai";

import DatePicker from "./date-picker/DatePicker";
import { getCountryNameWithCountryCode } from '../../config/static/country';
import countriesService from '../../services/countries.service';

import "./css/file-upload.css";

type FileUploadProps = {
  register: UseFormRegisterReturn;
  accept?: string;
  multiple?: boolean;
  children?: ReactNode;
  setFile: Function;
  setIsFileSelected: Function;
  setFileDate: Function;
  reset: Function;
  inputRef?: any;
  file?: any;
  onSubmit?: Function;
};

const FileUpload = (props: FileUploadProps) => {
  const { register, accept, multiple, children } = props;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { ref, ...rest } = register as {
    ref: (instance: HTMLInputElement | null) => void;
  };

  const handleClick = () => {
    // Single combined button: if a file is already chosen, this acts as Upload;
    // otherwise it opens the file picker (which then prompts for country/date).
    if (props.file) {
      if (props.onSubmit) props.onSubmit();
      return;
    }
    props.setFile(null);
    props.reset();
    return inputRef.current?.click();
  };
  const handleFileChange = (e: any) => {
    e.target &&
      e.target.files &&
      e.target.files.length > 0 &&
      e.target.files[0].name &&
      props.setFile(e.target.files[0].name);
    props.setIsFileSelected(true);
  };
  return (
    <InputGroup onClick={handleClick} onChange={handleFileChange}>
      <input
        type={"file"}
        style={{ fontSize: "1rem" }}
        multiple={multiple || false}
        hidden
        accept={accept}
        {...rest}
        ref={(e) => {
          ref(e);
          inputRef.current = e;
          if (props.inputRef) props.inputRef.current = e; // let the parent open the picker
        }}
      />
      <>{children}</>
    </InputGroup>
  );
};

type FormValues = {
  file_: FileList;
};

const UploadFile = (props: any) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>();
  const [file, setFile] = useState(null);
  const [isFileSelected, setIsFileSelected] = useState(false);
  const pickerRef = useRef<HTMLInputElement | null>(null);
  const [fileDate, setFileDate] = useState(new Date());
  const [fileCountry, setFileCountry] = useState('');
  const [countryOptions, setCountryOptions] = useState<any[]>([]);
  useEffect(() => {
    (countriesService.getCountries() as any).then((res: any) => {
      if (res && res.countries) setCountryOptions(res.countries.map((c: any) => ({ code: c.code, name: c.name })));
    });
  }, []);
  const [validationErrors, setValidationErrors] = useState({
    country: ''
  });
  //const { isOpen, onOpen, onClose } = useDisclosure();

  const onSubmit = handleSubmit((data) => {
    const postData = new FormData();
    const fileData = data.file_[0];
    postData.append("fileDate", fileDate.toString());    
    postData.append("fileCountry", fileCountry);
    postData.append("file", fileData);
    props.onSubmit(postData);
    reset();
    setFile(null);
    setFileDate(new Date());
    setFileCountry('');
  });

  const validateFiles = (value: FileList) => {
    if (value.length < 1) {
      return "Files is required";
    }
    for (const file of Array.from(value)) {
      const fsMb = file.size / (1024 * 1024);
      const MAX_FILE_SIZE = 10;
      if (fsMb > MAX_FILE_SIZE) {
        return "Max file size 10mb";
      }
    }
    return true;
  };

  return (
    <Flex direction="row">
      <Box p={2}>
        <form>
          <FormControl isInvalid={!!errors.file_} isRequired>
            <FileUpload
              accept={
                ".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
              }
              multiple
              register={register("file_", { validate: validateFiles })}
              setFile={setFile}
              setIsFileSelected={setIsFileSelected}
              setFileDate={setFileDate}
              inputRef={pickerRef}
              file={file}
              onSubmit={onSubmit}
              reset={() => {
                reset();
              }}
            >
              <Button
                fontSize={".8rem"}
                borderWidth={'1px'}
                borderColor={'rgb(94,94,114,.3)'}
                size="md"
                width={32}
                className={
                  errors.file_
                    ? "fileuploadButton inputValidationError"
                    : file
                    ? "fileuploadButton fileSelect"
                    : "fileuploadButton"
                }
                leftIcon={<Icon as={file ? AiOutlineUpload : FiFile} />}
              >
                {file ? "Upload" : "Choose File"}
              </Button>
            </FileUpload>
          </FormControl>
        </form>
      </Box>

      <Modal
        closeOnOverlayClick={false}
        isOpen={isFileSelected}
        onClose={() => {
          setIsFileSelected(false);
        }}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader textAlign="center">Please select the country and file date</ModalHeader>
          <ModalBody textAlign="center">
          <Table variant="unstyled">
            <Tbody>
              <Tr>
                <Td>Country</Td>
                <Td>
                  <Select
                        size="sm"
                        borderColor={validationErrors.country ? "red" : "#E2E8F0"}
                        value={fileCountry}
                        onChange={(e) => {
                          setFileCountry(e.target.value);
                          setValidationErrors({
                            ...validationErrors,
                            country: '',
                          });
                        }}
                        >
                        <option value=''> Choose the country </option>
                        {countryOptions.map((country: any, i: number) => {
                            return (
                            <option key={i} value={country.code}>
                                {country.name}
                            </option>
                            );
                        })}
                  </Select>
                </Td>
              </Tr>
              <Tr>
                <Td>Date</Td>
                <Td>
                  <DatePicker
                        selectedDate={fileDate}
                        showPopperArrow={true}
                        className={'DateWrapperClass_FileUpload'}
                        onChange={(date: any) => {
                          // setIsFileSelected(false);
                          setFileDate(date);
                        }}
                        // inline={true}
                      />
                </Td>
              </Tr>
            </Tbody>
          </Table>
          </ModalBody>
          <ModalFooter>
            <Button
              onClick = {(e) => {
                if(fileCountry === ''){
                  setValidationErrors({
                    ...validationErrors,
                    country: 'error',
                  });
                  return;
                }
                setIsFileSelected(false);
              }}
              
            >
              submit
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {file && (
        <Flex pl={2} direction="column">
          <Text fontSize={12} color={"green"} position={"relative"}>
            File Name &nbsp;&nbsp;&nbsp;: {file}
          </Text>
          <Text fontSize={12} color={"green"} position={"relative"}>
            File Country : {fileCountry ? getCountryNameWithCountryCode(fileCountry):fileCountry }
          </Text>
          <Text fontSize={12} color={"green"} position={"relative"}>
            File Date &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;: {fileDate.toDateString()}
          </Text>
        </Flex>
      )}
    </Flex>
  );
};

export default UploadFile;
